import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import {
  buildArInvitacionMpMetadata,
  buildArInvitacionProductId,
} from "@/lib/arInvitacionCheckout";
/**
 * Invitación Exclusiva Argentina — fullscreen VIP (MuxVideoPlayerWithCTA).
 * /ar/invitacion?ref=XXX | ?code=XXX
 * 1ª apertura: ventana corta (API valid_for_seconds). 2ª apertura: bloqueada en servidor.
 * Misma pestaña puede refrescar dentro de la ventana vía sessionStorage.
 *
 * Video: layout.mux_playback_id (admin) → ?playback= → VITE_AR_INVITACION_VIP_PLAYBACK_ID.
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { getArInvitacionAdminCode } from "@/config/adminInvitacion";
import {
  AR_INVITACION_CTA_LABEL,
  AR_INVITACION_CURRENCY,
  AR_INVITACION_MEMBERSHIP_NAME,
  AR_PLANS,
  AR_PLAN_VIP_SUBTITLE,
  type ArPlanTier,
} from "@/config/arInvitacionPlans";
import { formatArInvitacionVipPrice, resolveArInvitacionPlaybackId } from "@/lib/arInvitacionVideo";
import {
  trackPageViewPostHog,
  trackPaymentModalPostHog,
} from "@/lib/posthogAnalytics";
import { AlertCircle, Clock, ExternalLink, Loader2, Lock, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  validateInvitationValue,
  type InvitationValidateApiResponse,
  type InvitationValidationMode,
} from "@/lib/api";

const INVITATION_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

const SESSION_CACHE_KEY = "ar_invitation_session_v2";

type SessionCache = {
  code: string;
  validUntilIso: string;
  validForSeconds: number;
  invitationCode: string;
  layout?: InvitationValidateApiResponse["layout"];
};

type InvitationState = "loading" | "valid" | "expired" | "invalid" | "used";

const FALLBACK_CODES = new Set(["DELFU2024", "ARGENTINA", "ULTRAVIP", "BSAS"]);

function readSessionCache(code: string): SessionCache | null {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionCache;
    if (parsed.code !== code.toUpperCase()) return null;
    if (new Date(parsed.validUntilIso).getTime() <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(cache: SessionCache): void {
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* best-effort */
  }
}

async function createMercadoPagoSubscription(
  invitationCode: string,
  buyerEmail: string,
): Promise<{ success: true; init_point: string } | { success: false; error: string }> {
  const plan: ArPlanTier = "vip";
  const { price } = AR_PLANS[plan];
  const productName = AR_INVITACION_MEMBERSHIP_NAME;
  try {
    const response = await fetch("/api/mercadopago/create-subscription", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        productId: buildArInvitacionProductId(invitationCode),
        productName,
        reason: productName,
        price,
        currency: AR_INVITACION_CURRENCY,
        country: "AR",
        successPath: "/ar/invitacion/success",
        failurePath: "/ar/invitacion/error",
        pendingPath: "/ar/invitacion/pending",
        buyerEmail,
        metadata: buildArInvitacionMpMetadata(invitationCode),
      }),
    });
    const data = await response.json();
    if (!response.ok || !data.init_point) {
      return { success: false, error: data.error || "Error al crear checkout" };
    }
    return { success: true, init_point: data.init_point };
  } catch {
    return { success: false, error: "Error de conexión. Intenta de nuevo." };
  }
}

function ErrorScreen({
  title,
  message,
  onHome,
}: {
  title: string;
  message: string;
  onHome: () => void;
}) {
  return (
    <div
      className="h-dvh max-h-dvh overflow-hidden relative flex items-center justify-center px-4 bg-black text-white"
      style={{ fontFamily: INVITATION_FONT_FAMILY }}
    >
      <div className="w-full max-w-md mx-auto text-center space-y-6">
        <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
          <Lock className="w-10 h-10" />
        </div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-gray-400 text-sm leading-relaxed">{message}</p>
        <button
          type="button"
          onClick={onHome}
          className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 font-semibold inline-flex items-center justify-center gap-2"
        >
          Volver al inicio
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function InvitacionExclusivaArgentina() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const inviteRef = searchParams.get("ref")?.trim() || "";
  const inviteCode = searchParams.get("code")?.trim() || searchParams.get("c")?.trim() || "";
  const inviteMode: InvitationValidationMode | null = inviteRef
    ? "ref"
    : inviteCode
      ? "code"
      : null;
  const inviteValue = inviteMode === "ref" ? inviteRef : inviteCode;

  const [invitationState, setInvitationState] = useState<InvitationState>("loading");
  const [invalidMessage, setInvalidMessage] = useState(
    "El código de invitación no existe o es incorrecto. Verifica que tengas el enlace correcto.",
  );
  const [invitationCode, setInvitationCode] = useState("");
  const [layout, setLayout] = useState<InvitationValidateApiResponse["layout"]>();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [checkoutState, setCheckoutState] = useState<
    "idle" | "email" | "creating" | "redirecting" | "error"
  >("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const validUntilRef = useRef<string | null>(null);
  const checkoutStateRef = useRef(checkoutState);
  checkoutStateRef.current = checkoutState;

  const vipPrice = AR_PLANS.vip.price;
  const priceLabel = formatArInvitacionVipPrice(vipPrice);

  const playbackId = useMemo(
    () => resolveArInvitacionPlaybackId({ layout, searchParams }),
    [layout, searchParams],
  );

  const ctaLabel = AR_INVITACION_CTA_LABEL;
  const ctaSubtitle = `${priceLabel} · ${AR_PLAN_VIP_SUBTITLE}`;

  const pauseCountdown = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(
    (validUntilIso: string) => {
      validUntilRef.current = validUntilIso;
      if (tickRef.current) clearInterval(tickRef.current);

      const tick = () => {
        if (
          checkoutStateRef.current === "email" ||
          checkoutStateRef.current === "creating" ||
          checkoutStateRef.current === "redirecting"
        ) {
          return;
        }
        const remaining = Math.max(
          0,
          Math.floor((new Date(validUntilIso).getTime() - Date.now()) / 1000),
        );
        setSecondsLeft(remaining);
        if (remaining <= 0) {
          if (tickRef.current) clearInterval(tickRef.current);
          setInvitationState("used");
        }
      };
      tick();
      tickRef.current = setInterval(tick, 1000);
    },
    [],
  );

  useEffect(() => {
    const validate = async () => {
      if (!inviteMode || !inviteValue) {
        setInvitationState("invalid");
        return;
      }

      const codeUpper = inviteValue.toUpperCase();
      const adminCode = getArInvitacionAdminCode();

      if (codeUpper === adminCode) {
        setInvitationCode(adminCode);
        setInvitationState("valid");
        const far = new Date();
        far.setFullYear(far.getFullYear() + 10);
        startCountdown(far.toISOString());
        return;
      }

      const cached = readSessionCache(codeUpper);
      if (cached) {
        setInvitationCode(cached.invitationCode);
        if (cached.layout) setLayout(cached.layout);
        setInvitationState("valid");
        startCountdown(cached.validUntilIso);
        return;
      }

      // FALLBACK_CODES como allowlist: se aplica DESPUÉS de la API (si dice
      // inválido o falla la red), no antes. Antes el guard isApiUrlConfigured()
      // era false en prod (solo detecta el override de dev) → la API real nunca
      // se llamaba y los códigos generados desde el admin no funcionaban.
      const grantFallback = () => {
        const validUntil = new Date(Date.now() + 60 * 1000);
        setInvitationCode(codeUpper);
        setInvitationState("valid");
        writeSessionCache({
          code: codeUpper,
          validUntilIso: validUntil.toISOString(),
          validForSeconds: 60,
          invitationCode: codeUpper,
        });
        startCountdown(validUntil.toISOString());
      };

      try {
        const data: InvitationValidateApiResponse = await validateInvitationValue(
          codeUpper,
          inviteMode,
        );
        if (!data.valid) {
          if (FALLBACK_CODES.has(codeUpper)) {
            grantFallback();
            return;
          }
          setInvitationState(data.message?.includes("expir") ? "expired" : "used");
          setInvalidMessage(
            data.message?.trim() ||
              "Esta invitación ha alcanzado el límite máximo de usos o tu sesión ha expirado.",
          );
          return;
        }

        const validUntilIso =
          data.valid_until_iso ||
          new Date(Date.now() + (data.valid_for_seconds ?? 60) * 1000).toISOString();
        const resolvedCode = data.code ?? codeUpper;

        if (data.layout) setLayout(data.layout);
        setInvitationCode(resolvedCode);
        setInvitationState("valid");
        writeSessionCache({
          code: codeUpper,
          validUntilIso,
          validForSeconds: data.valid_for_seconds ?? 60,
          invitationCode: resolvedCode,
          ...(data.layout ? { layout: data.layout } : {}),
        });
        startCountdown(validUntilIso);
      } catch {
        if (FALLBACK_CODES.has(codeUpper)) {
          grantFallback();
          return;
        }
        setInvitationState("invalid");
        setInvalidMessage(
          "No se pudo verificar la invitación. Verifica tu conexión e intenta de nuevo.",
        );
      }
    };

    void validate();
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [inviteMode, inviteValue, startCountdown]);

  useEffect(() => {
    if (invitationState !== "valid") return;
    trackPageViewPostHog("/ar/invitacion", AR_INVITACION_MEMBERSHIP_NAME, "ar_invitacion_vip");
  }, [invitationState]);

  useEffect(() => {
    if (invitationState !== "valid") return;
    const origOverflow = document.body.style.overflow;
    const origHeight = document.body.style.height;
    const origPosition = document.body.style.position;
    const origWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = origOverflow;
      document.body.style.height = origHeight;
      document.body.style.position = origPosition;
      document.body.style.width = origWidth;
    };
  }, [invitationState]);

  useEffect(() => {
    if (checkoutState === "email" || checkoutState === "creating" || checkoutState === "redirecting") {
      pauseCountdown();
      return;
    }
    if (checkoutState === "idle" && validUntilRef.current && invitationState === "valid") {
      startCountdown(validUntilRef.current);
    }
  }, [checkoutState, invitationState, pauseCountdown, startCountdown]);

  const handleCTAClick = useCallback(
    (_url: string) => {
      if (!invitationCode) return;
      if (checkoutState !== "idle" && checkoutState !== "email") return;
      if (checkoutState === "idle") {
        trackPaymentModalPostHog("open", "delfuocampo_ar_invitacion");
        setCheckoutState("email");
        setCheckoutError(null);
        return;
      }
      const trimmed = email.trim();
      if (!trimmed) {
        setCheckoutError("Ingresá tu email para continuar.");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setCheckoutError("Ingresá un email válido.");
        return;
      }
      setCheckoutState("creating");
      setCheckoutError(null);
      void createMercadoPagoSubscription(invitationCode, trimmed).then((result) => {
        if (result.success) {
          setCheckoutState("redirecting");
          window.location.href = result.init_point;
        } else {
          setCheckoutState("error");
          setCheckoutError(result.error);
        }
      });
    },
    [checkoutState, email, invitationCode],
  );

  if (invitationState === "loading") {
    return (
      <div
        className="h-dvh flex items-center justify-center bg-black text-white"
        style={{ fontFamily: INVITATION_FONT_FAMILY }}
      >
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-purple-400" />
          <p className="text-gray-400 text-sm">Verificando invitación...</p>
        </div>
      </div>
    );
  }

  if (invitationState === "invalid") {
    return (
      <ErrorScreen
        title="Invitación no válida"
        message={invalidMessage}
        onHome={() => navigate("/")}
      />
    );
  }

  if (invitationState === "expired") {
    return (
      <ErrorScreen
        title="Invitación expirada"
        message="Esta invitación ha expirado y ya no está disponible. Contactá para obtener una nueva."
        onHome={() => navigate("/")}
      />
    );
  }

  if (invitationState === "used") {
    return (
      <ErrorScreen
        title="Invitación no disponible"
        message={
          invalidMessage ||
          "Esta invitación ya fue abierta o tu tiempo de acceso terminó. Solo puede usarse una vez."
        }
        onHome={() => navigate("/")}
      />
    );
  }

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const showCountdown =
    secondsLeft > 0 &&
    secondsLeft < 86400 &&
    checkoutState !== "email" &&
    checkoutState !== "creating" &&
    checkoutState !== "redirecting";

  return (
    <div className="w-full h-dvh max-h-dvh overflow-hidden bg-black relative">
      {showCountdown && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-xl bg-black/70 text-white text-center backdrop-blur-sm border border-amber-500/30"
          aria-live="polite"
        >
          <span className="text-xs uppercase tracking-wider text-amber-200/90 block mb-0.5 inline-flex items-center gap-1 justify-center">
            <Clock className="w-3 h-3" />
            {AR_INVITACION_MEMBERSHIP_NAME}
          </span>
          <span className="font-mono text-lg font-bold tabular-nums">
            {mm}:{ss}
          </span>
        </div>
      )}

      {checkoutState === "email" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md px-4">
          <div className="w-full max-w-sm space-y-5 rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/5 p-6 shadow-2xl">
            <div className="text-center space-y-2">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">{AR_INVITACION_MEMBERSHIP_NAME}</h2>
              <p className="text-sm text-white/70 leading-relaxed">
                {BRAND_PERSON_NAME
                  ? `Unite al grupo exclusivo de ${BRAND_PERSON_NAME}`
                  : "Unite al grupo exclusivo"}
              </p>
              <p className="text-2xl font-bold text-amber-300 tabular-nums">{priceLabel}</p>
              <p className="text-xs text-white/50">{AR_PLAN_VIP_SUBTITLE}</p>
            </div>
            <div className="space-y-3">
              <label htmlFor="ar-inv-email" className="sr-only">
                Email
              </label>
              <input
                id="ar-inv-email"
                type="email"
                autoComplete="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setCheckoutError(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleCTAClick("")}
                className="w-full rounded-xl border border-white/20 bg-black/40 px-4 py-3.5 text-white placeholder:text-white/40 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
              />
              {checkoutError && (
                <p className="text-center text-sm text-red-300 flex items-center justify-center gap-1">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {checkoutError}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  trackPaymentModalPostHog("close", "delfuocampo_ar_invitacion");
                  setCheckoutState("idle");
                  setEmail("");
                  setCheckoutError(null);
                }}
                className="flex-1 rounded-xl bg-white/10 px-4 py-3 text-white text-sm hover:bg-white/20 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleCTAClick("")}
                className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 text-white text-sm font-semibold hover:from-amber-400 hover:to-orange-400 transition-colors shadow-lg shadow-amber-500/20"
              >
                Pagar con Mercado Pago
              </button>
            </div>
            <p className="text-center text-[11px] text-white/40">
              Suscripción mensual · cancelás cuando quieras
            </p>
          </div>
        </div>
      )}

      {checkoutState === "creating" && (
        <div className="absolute inset-0 z-[70] flex flex-col items-center justify-center gap-3 bg-black/85 backdrop-blur-md">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          <p className="text-white text-sm">Preparando checkout...</p>
        </div>
      )}
      {checkoutState === "redirecting" && (
        <div className="absolute inset-0 z-[70] flex flex-col items-center justify-center gap-3 bg-black/85 backdrop-blur-md">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          <p className="text-white text-lg">Redirigiendo a Mercado Pago...</p>
        </div>
      )}
      {checkoutState === "error" && checkoutError && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md px-4">
          <div className="text-center max-w-sm space-y-4 rounded-2xl border border-white/10 bg-white/5 p-6">
            <p className="text-white/90">{checkoutError}</p>
            <button
              type="button"
              onClick={() => {
                setCheckoutState("idle");
                setCheckoutError(null);
              }}
              className="rounded-xl bg-white/20 px-5 py-2.5 text-white text-sm hover:bg-white/30"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {playbackId ? (
        <>
          <MuxPosterBackdrop
            key={`poster-${playbackId}`}
            playbackId={playbackId}
            fitMode="smartcrop"
            hidden={videoPlaying}
          />
          <MuxVideoPlayerWithCTA
            playbackId={playbackId}
            forceManualPlaybackId
            variant="fullscreen"
            countryCode="AR"
            language="es"
            hideUnmutePrompt
            ctaLabel={ctaLabel}
            ctaSubtitle={ctaSubtitle}
            ctaUrl="/ar/invitacion"
            onCTAClick={handleCTAClick}
            onPlay={() => setVideoPlaying(true)}
          />
        </>
      ) : (
        <div className="h-full flex items-center justify-center px-6 text-center text-white/70">
          <p className="text-sm">Configurá el video VIP (VITE_AR_INVITACION_VIP_PLAYBACK_ID).</p>
        </div>
      )}
    </div>
  );
}